import React, { useEffect, useState } from "react";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { ReactDatePickerProps } from "react-datepicker";
import voca from "voca";
import { useTheme, useMediaQuery } from "@mui/material";

import FieldLabel from "../components/FieldLabel";
import PortalDatePicker from "./PortalDatePicker";
import TimeField from "./TimeField";

import { usePreferences } from "../../../../utility/usePreferences";
import flex from "../../../../styles/flex";
 
export interface PortalDatePickerProps extends ReactDatePickerProps {
    label?: string,
    defaultValue?: boolean,
    initializeFilled?: boolean,
    testId?: string,
    readOnly?: boolean,
}

interface PortalDateTimeProps {
    label: string,
    value: string,
    onChange: (value: string) => void;
    testId?: string,
    readOnly?: boolean,
    defaultMinDate?: boolean,
    dateProps: PortalDatePickerProps,
    timeProps: {
        onChange: (value: string) => void;
        value: string,
        label: string,
        localise?: boolean,
    }
}

export default function PortalDateTime(props: PortalDateTimeProps) {
    const { dateProps, timeProps } = props;
    const { timeZone } = usePreferences();
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const timeZoneConvertedDate = toZonedTime(fromZonedTime(new Date(), localTimeZone), timeZone);

    const [fakeDateUtc, setFakeDateUtc] = useState<string>();
    const [fakeTimeUtc, setFakeTimeUtc] = useState<string>();
    //const [dateValue, setDateValue] = useState<Date>();
    //const minDateValue = props.defaultMinDate ? timeZoneConvertedDate : null;

    const theme = useTheme();
    const isSmallDevice = useMediaQuery(theme.breakpoints.down("xs"));

    useEffect(() => {
        if (modifiedDateProps.initializeFilled && !fakeTimeUtc) {
            handleTimeChange(timeProps.value.toString());
        }

        if (fakeDateUtc && fakeTimeUtc) {
            const fakeLocalDateTimeUTC = `${fakeDateUtc}${fakeTimeUtc}`;
            const dateValueToSet = fromZonedTime(fakeLocalDateTimeUTC, timeZone).toISOString();

            props.onChange(dateValueToSet);
        }
    }, [fakeDateUtc, fakeTimeUtc])

    const handleDateChange = (val: string | Date | null) => {
        if (!val) {
            return;
        }

        const newDate = new Date(val);

        const { year, month, date } = {
            year: newDate.getFullYear(),
            month: newDate.getMonth() + 1,
            date: newDate.getDate(),
        }

        setFakeDateUtc(voca.sprintf("%02d-%02d-%02d", year, month, date));
    }

    const handleTimeChange = (val: string | null) => {
        if (!val) {
            return;
        }

        const time = val.split("T")[1];
        const [ hour, minute ] = time.split(":").map(parseInt);

        setFakeTimeUtc(voca.sprintf("T%02d:%02d:00.000", hour, minute));
    }

    if (props.defaultMinDate) {
        if (!dateProps.minDate || dateProps.minDate < timeZoneConvertedDate) {
            dateProps.minDate = timeZoneConvertedDate;
        }
    
        if (dateProps.selected && dateProps.selected < timeZoneConvertedDate) {
            dateProps.selected = timeZoneConvertedDate;
        }

        if (dateProps.selected && dateProps.minDate && dateProps.selected < dateProps.minDate) {
            dateProps.selected = dateProps.minDate
        }
    }

    const modifiedDateProps = {...dateProps};

    modifiedDateProps.minDate = toZonedTime(modifiedDateProps.minDate || "", timeZone);
    modifiedDateProps.maxDate = toZonedTime(modifiedDateProps.maxDate || "", timeZone);

    const modifiedTimeProps = {...timeProps};
    
    // if initializeFilled is set and a default is required, set it to midnight.
    if (modifiedDateProps.initializeFilled && !fakeTimeUtc) {
        modifiedTimeProps.localise = false;
        const midnight = new Date(new Date().setHours(0,0,0,0));
        modifiedTimeProps.value = midnight.toString();
    }

    return (<>
        <div style={isSmallDevice ? flex.flexColumns : flex.flexRows}>
            <div style={{flex: 1, textAlign: isSmallDevice ? "center" : "left", paddingRight: 20}}>
                <FieldLabel>{dateProps.label}</FieldLabel>
                <PortalDatePicker {...modifiedDateProps} testId={props.testId ? `${props.testId}-date` : undefined} onChange={(val) => handleDateChange(val)}/>
            </div>
            <div style={{height: 20, width: 0}}></div>
            <div style={{flex: 1, textAlign: isSmallDevice ? "center" : "left"}}>
                <FieldLabel>{timeProps.label}</FieldLabel>
                <TimeField {...modifiedTimeProps} testId={props.testId ? `${props.testId}-time` : undefined} readOnly={props.readOnly} onChange={(val) => handleTimeChange(val)}/>
            </div>
        </div>
    </>)
}
